import React from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  ChevronDown,
  X,
  Loader2,
  CheckCircle2,
  Mail,
  User,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import PhoneField from "../ui/PhoneField.jsx";
import GoogleContinueButton from "./GoogleContinueButton.jsx";
import { STORAGE_KEYS } from "../../utils/constants.js";
import { shortlistPathForCategory } from "../../utils/sourcingCategory.js";
import { borrowerSignUp } from "../../api/borrowerPortal/borrowerSignUp.js";
import { loginWithGoogleToken } from "../../api/borrowerPortal/googleLogin.js";
import { prepareForLogin } from "../../api/client.js";
import { completeBorrowerLogin, hasDirectLoginPayload } from "../../pages/borrower/borrowerAuthUtils.js";
import { extractAuthPayload } from "../../utils/loginAuth.js";
import { isValidPhone, PHONE_ERROR_MOBILE } from "../../utils/validatePhone.js";

const promptSignUpSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Please enter a valid email"),
  phone: z
    .string()
    .min(1, PHONE_ERROR_MOBILE)
    .refine((v) => isValidPhone(v), { message: PHONE_ERROR_MOBILE }),
  agree: z.literal(true, {
    errorMap: () => ({ message: "You must agree to the Terms and Privacy Policy" }),
  }),
});

function getPromptSignUpMessage(response) {
  if (!response || typeof response !== "object") return null;
  const data = response.data != null ? response.data : response;
  if (typeof data !== "object") return null;
  const msg = data.message;
  if (Array.isArray(msg) && msg.length > 1 && typeof msg[1] === "string") return msg[1].trim();
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  try {
    const raw = data._server_messages;
    if (typeof raw === "string") {
      const arr = JSON.parse(raw);
      const first = arr?.[0];
      if (typeof first === "string") {
        const parsed = JSON.parse(first);
        if (parsed?.message && typeof parsed.message === "string") return parsed.message.trim();
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

function isAlreadyRegisteredEmailError(value) {
  const s = String(value || "").toLowerCase();
  return (
    s.includes("already registered") ||
    s.includes("already exists") ||
    s.includes("user already exists") ||
    s.includes("duplicate") ||
    s.includes("email already")
  );
}

/**
 * Guest sign-up modal shown on public sourcing pages.
 * Email sign-up keeps the modal open on the "Email confirmation" screen;
 * Google sign-up logs the borrower in and redirects.
 */
export default function SourcingSignupPrompt({
  open,
  onClose,
  onSignupSuccess,
  startOnForm = false,
  category = "BTL",
  title = "Keep comparing buy-to-let products",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [showFormStep, setShowFormStep] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(promptSignUpSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      agree: false,
    },
  });

  const phoneValue = watch("phone");
  const busy = isSubmitting || googleLoading;

  React.useEffect(() => {
    if (!open) {
      setShowFormStep(false);
      return;
    }
    setShowFormStep(Boolean(startOnForm));
    setServerError("");
    setSuccessMessage("");
    setGoogleLoading(false);
    reset({
      fullName: "",
      email: "",
      phone: "",
      agree: false,
    });
  }, [open, reset, startOnForm]);

  React.useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const finishGoogleSignup = React.useCallback(
    async (idToken) => {
      setServerError("");
      setGoogleLoading(true);
      await prepareForLogin();
      try {
        const { data, headers, payload } = await loginWithGoogleToken(idToken, {
          role: "Borrower",
        });
        const authPayload = extractAuthPayload(data) || payload;
        const em = String(authPayload?.user || authPayload?.email || "")
          .trim()
          .toLowerCase();
        if (!em || !hasDirectLoginPayload(data)) {
          setServerError("Unexpected response from Google sign-up. Please try again.");
          return;
        }
        try {
          localStorage.setItem("ds_signup_email", em);
          localStorage.setItem(STORAGE_KEYS.POST_AUTH_REDIRECT, shortlistPathForCategory(category));
        } catch {
          /* ignore */
        }
        onSignupSuccess?.();
        await completeBorrowerLogin(
          em,
          { data, headers },
          authPayload,
          true,
          navigate,
          location,
          null,
          { viaGoogle: true, sourcingCategory: category }
        );
      } catch (err) {
        console.error("Google signup (sourcing prompt) error:", err);
        if (err?.message === "LOGIN_CREDENTIALS_MISSING") {
          setServerError("Could not establish your session. Please try again.");
          return;
        }
        setServerError(err?.message || "Google sign-up failed");
      } finally {
        setGoogleLoading(false);
      }
    },
    [navigate, location, onSignupSuccess, category]
  );

  const onSubmit = async (data) => {
    setServerError("");
    setSuccessMessage("");
    try {
      const response = await borrowerSignUp({
        email: data.email.trim().toLowerCase(),
        full_name: data.fullName.trim(),
        phone: data.phone,
        redirect_to: "/borrower/signin",
      });
      const displayMessage = getPromptSignUpMessage(response);
      setSuccessMessage(
        displayMessage || "Account created. Please check your email for verification."
      );
      try {
        localStorage.setItem("ds_signup_email", data.email.trim().toLowerCase());
        localStorage.setItem(STORAGE_KEYS.POST_AUTH_REDIRECT, shortlistPathForCategory(category));
      } catch {
        /* ignore */
      }
      onSignupSuccess?.({ keepOpen: true });
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      const displayError = getPromptSignUpMessage(errData) || err?.message || "Sign up failed.";
      if (isAlreadyRegisteredEmailError(displayError)) {
        setError("email", {
          type: "server",
          message: "This email is already registered. Please sign in instead.",
        });
      }
      setServerError(displayError);
    }
  };

  if (!open) return null;

  const inputClass = (hasError) =>
    `w-full rounded-lg border px-2.5 py-2 sm:rounded-xl sm:px-3 sm:py-2.5 pr-9 sm:pr-10 text-[16px] sm:text-base focus:outline-none focus:ring-2 focus:ring-sky-200 ${
      hasError ? "border-rose-300" : "border-slate-300"
    }`;

  return createPortal(
    <div
      className="fixed inset-0 z-[960] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sourcing-signup-prompt-title"
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className={
          "relative z-10 flex w-full max-w-[320px] sm:max-w-md flex-col overflow-hidden bg-white shadow-2xl " +
          "rounded-2xl border border-slate-200 " +
          (showFormStep ? "max-h-[min(85dvh,520px)] sm:max-h-[min(88dvh,640px)]" : "")
        }
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-20 inline-flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"
          aria-label="Close signup prompt"
        >
          <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>

        {!showFormStep ? (
          <div className="flex flex-col items-center text-center px-5 pt-8 pb-5 sm:px-8 sm:pt-10 sm:pb-7 overflow-visible">
            <div className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-sky-100 text-sky-600" aria-hidden>
              <ShieldCheck className="h-7 w-7 sm:h-8 sm:w-8" />
            </div>
            <h2
              id="sourcing-signup-prompt-title"
              className="mt-4 text-[17px] sm:text-xl font-bold text-slate-900 leading-snug max-w-[260px] sm:max-w-none"
            >
              {title}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-slate-600 leading-snug max-w-[280px] sm:max-w-none">
              Register for free to continue sourcing products.
            </p>
            <button
              type="button"
              onClick={() => setShowFormStep(true)}
              className="mt-5 sm:mt-6 w-full inline-flex min-h-[44px] sm:min-h-[48px] items-center justify-center rounded-xl bg-sky-500 px-4 py-3 text-sm sm:text-base font-bold text-white hover:bg-sky-600 transition-colors shadow-sm"
              disabled={busy}
            >
              Register for free
            </button>

            <div className="relative my-4 w-full">
              <div className="absolute inset-0 flex items-center" aria-hidden>
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wide">
                <span className="bg-white px-3 text-slate-400">or</span>
              </div>
            </div>

            <GoogleContinueButton
              remountKey={`welcome-${open}`}
              disabled={busy}
              className="max-w-full"
              onCredential={(credential) => finishGoogleSignup(credential)}
              onError={setServerError}
            />
            <p className="mt-2 text-[10px] sm:text-xs text-slate-500 leading-snug">
              By continuing with Google, you agree to our{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                Terms and Privacy Policy
              </a>
              .
            </p>
            {serverError ? <p className="mt-2 text-[11px] sm:text-sm text-rose-600">{serverError}</p> : null}
            {googleLoading ? (
              <p className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Completing Google sign-up…
              </p>
            ) : null}

            <p className="mt-4 text-xs sm:text-sm text-slate-600">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate("/borrower/signin");
                }}
                className="font-semibold text-sky-700 hover:underline"
                disabled={busy}
              >
                Sign in
              </button>
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 min-h-[36px] text-xs sm:text-sm text-slate-500 hover:text-slate-700"
              disabled={busy}
            >
              Not now
            </button>
          </div>
        ) : (
          <>
            <div className="flex-shrink-0 flex items-center gap-2 border-b border-slate-100 bg-white px-3 pt-2.5 pb-2 sm:px-5 sm:pt-4 sm:pb-3 pr-10 sm:pr-12">
              <button
                type="button"
                onClick={() => setShowFormStep(false)}
                className="inline-flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                aria-label="Back"
              >
                <ChevronDown className="h-4 w-4 rotate-90" />
              </button>
              <h2 className="text-sm sm:text-lg font-semibold text-slate-900 leading-tight">
                Create a free borrower account
              </h2>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-2 sm:px-5 sm:py-4">
              {successMessage ? (
                <div className="flex flex-col items-center text-center py-2 sm:py-6">
                  <div className="rounded-full bg-emerald-100 p-2 mb-1.5 sm:p-3 sm:mb-3" aria-hidden>
                    <CheckCircle2 className="h-7 w-7 sm:h-10 sm:w-10 text-emerald-600" />
                  </div>
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900">Email confirmation</h3>
                  <p className="mt-1 text-xs sm:text-sm text-slate-700 font-medium max-w-sm">{successMessage}</p>
                  <p className="mt-1 text-[10px] sm:text-xs text-slate-500">
                    Check your email for a verification link to set your password and sign in.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate("/borrower/signin");
                    }}
                    className="mt-3 sm:mt-4 w-full inline-flex min-h-[40px] sm:min-h-[48px] items-center justify-center gap-2 rounded-lg sm:rounded-xl bg-sky-600 px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base font-semibold text-white hover:bg-sky-700"
                  >
                    Go to Sign in
                  </button>
                </div>
              ) : (
                <>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-2 sm:space-y-3.5">
                    <label className="block">
                      <span className="mb-0.5 sm:mb-1 block text-[11px] sm:text-sm font-medium text-slate-700">Full Name</span>
                      <div className="relative">
                        <input
                          type="text"
                          autoComplete="name"
                          {...register("fullName")}
                          className={inputClass(Boolean(errors.fullName))}
                          placeholder="Jane Borrower"
                          disabled={busy}
                        />
                        <User className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400 pointer-events-none" />
                      </div>
                      {errors.fullName ? (
                        <p className="mt-0.5 text-[10px] sm:text-[11px] text-rose-600">{errors.fullName.message}</p>
                      ) : null}
                    </label>

                    <label className="block">
                      <span className="mb-0.5 sm:mb-1 block text-[11px] sm:text-sm font-medium text-slate-700">Email</span>
                      <div className="relative">
                        <input
                          type="email"
                          autoComplete="email"
                          inputMode="email"
                          {...register("email")}
                          className={inputClass(Boolean(errors.email))}
                          placeholder="you@example.com"
                          disabled={busy}
                        />
                        <Mail className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400 pointer-events-none" />
                      </div>
                      {errors.email ? (
                        <p className="mt-0.5 text-[10px] sm:text-[11px] text-rose-600">{errors.email.message}</p>
                      ) : null}
                    </label>

                    <div className="block">
                      <label htmlFor="sourcing-prompt-signup-phone" className="mb-0.5 sm:mb-1 block text-[11px] sm:text-sm font-medium text-slate-700">
                        Phone Number
                      </label>
                      <PhoneField
                        id="sourcing-prompt-signup-phone"
                        value={phoneValue}
                        onChange={(next) => setValue("phone", next, { shouldValidate: true, shouldTouch: true })}
                        disabled={busy}
                      />
                    </div>

                    <div className="block">
                      <label className="inline-flex items-start gap-1.5 sm:gap-2 text-[11px] sm:text-sm text-slate-700">
                        <input
                          type="checkbox"
                          {...register("agree")}
                          disabled={busy}
                          className="mt-0.5 h-3.5 w-3.5 sm:h-5 sm:w-5 shrink-0 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        <span className="leading-snug">
                          I agree to the{" "}
                          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                            Terms and Privacy Policy
                          </a>
                        </span>
                      </label>
                      {errors.agree ? (
                        <p className="mt-0.5 text-[10px] sm:text-[11px] text-rose-600">{errors.agree.message}</p>
                      ) : null}
                    </div>

                    {serverError ? <p className="text-[11px] sm:text-sm text-rose-600">{serverError}</p> : null}

                    <button
                      type="submit"
                      disabled={busy || !isValidPhone(phoneValue)}
                      className="inline-flex w-full min-h-[40px] sm:min-h-[48px] items-center justify-center gap-1.5 sm:gap-2 rounded-lg sm:rounded-xl bg-sky-500 px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base font-semibold text-white hover:bg-sky-600 disabled:opacity-70"
                    >
                      {isSubmitting ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                      {isSubmitting ? "Creating account..." : "Register for free"}
                    </button>
                  </form>

                  <div className="relative my-3 sm:my-4">
                    <div className="absolute inset-0 flex items-center" aria-hidden>
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase tracking-wide">
                      <span className="bg-white px-3 text-slate-400">or</span>
                    </div>
                  </div>

                  <GoogleContinueButton
                    remountKey={`form-${open}`}
                    disabled={busy}
                    className="max-w-full"
                    onCredential={(credential) => finishGoogleSignup(credential)}
                    onError={setServerError}
                  />
                  <p className="mt-1.5 text-[10px] sm:text-xs text-slate-500 text-center leading-snug">
                    By continuing with Google, you agree to our{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                      Terms and Privacy Policy
                    </a>
                    .
                  </p>
                  {googleLoading ? (
                    <p className="mt-2 text-xs text-slate-500 inline-flex items-center justify-center gap-1.5 w-full">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Completing Google sign-up…
                    </p>
                  ) : null}

                  <p className="mt-2 sm:mt-4 text-center text-[11px] sm:text-sm text-slate-600">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate("/borrower/signin");
                      }}
                      className="font-semibold text-sky-700 hover:underline"
                      disabled={busy}
                    >
                      Sign in
                    </button>
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-0.5 mb-0.5 w-full min-h-[32px] sm:min-h-[40px] text-center text-[11px] sm:text-sm text-slate-500 hover:text-slate-700"
                    disabled={busy}
                  >
                    Not now
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
